// Persistence for interactive task-list checkboxes rendered inside doc
// pages. API-backed shared state via /api/devops/doc-checks — team-wide,
// not per-browser. Fetched on subscribe, mutated by toggleChecked, cached
// module-level so the progress bar can read synchronously.
//
// A checkbox ID is a stable slug derived from the task text: whitespace
// collapsed, punctuation stripped, lowercased. Position-based IDs would
// invalidate on reorder, which for a definition-of-done tracker is
// exactly the wrong failure mode.
import { readCheckedIds, writeChecked } from "../api/doc-checks";

export function checkboxIdFromText(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

type Listener = (checked: Set<string>) => void;

// Module-level state. One entry per doc that has been fetched or mutated
// this session; listeners keyed by docPath.
const cache = new Map<string, Set<string>>();
const listeners = new Map<string, Set<Listener>>();
const inFlightFetches = new Map<string, Promise<Set<string>>>();

function notify(docPath: string): void {
  const set = cache.get(docPath) ?? new Set<string>();
  const subs = listeners.get(docPath);
  if (!subs) return;
  // Emit a fresh Set so downstream `useEffect` dependency checks see a
  // reference change and subscribers using it as state trigger a re-render.
  const snapshot = new Set(set);
  for (const l of subs) l(snapshot);
}

/** Kick off (or reuse) a fetch of the ticked ids for one doc. */
function ensureFetched(docPath: string): Promise<Set<string>> {
  const existing = inFlightFetches.get(docPath);
  if (existing) return existing;
  const p = readCheckedIds(docPath).then((set) => {
    cache.set(docPath, set);
    inFlightFetches.delete(docPath);
    notify(docPath);
    return set;
  });
  inFlightFetches.set(docPath, p);
  return p;
}

/** Async read — resolves with the current ticked set (fetches if needed). */
export async function getCheckedIds(docPath: string): Promise<Set<string>> {
  if (cache.has(docPath)) return new Set(cache.get(docPath)!);
  return new Set(await ensureFetched(docPath));
}

/**
 * Sync read from the module-level cache. Returns an empty Set when the
 * doc has not been fetched yet — callers should also subscribe so they
 * re-render when the fetch completes.
 */
export function checkedIdsSync(docPath: string): Set<string> {
  return new Set(cache.get(docPath) ?? new Set<string>());
}

/**
 * Persist a toggle to the backend, update the cache, and notify subscribers.
 * Throws on PUT failure — callers using optimistic UI catch it to revert.
 */
export async function toggleChecked(
  docPath: string,
  checkboxId: string,
  checked: boolean,
): Promise<void> {
  await writeChecked(docPath, checkboxId, checked);
  const current = cache.get(docPath) ?? new Set<string>();
  if (checked) current.add(checkboxId);
  else current.delete(checkboxId);
  cache.set(docPath, current);
  notify(docPath);
}

/** Alias — kept for callers that read the older name. */
export async function setChecked(
  docPath: string,
  checkboxId: string,
  checked: boolean,
): Promise<void> {
  return toggleChecked(docPath, checkboxId, checked);
}

/**
 * Subscribe to a doc's ticked set. Fires once immediately with whatever
 * the cache has (empty set on first sight), triggers a fetch if the doc
 * has not been loaded yet, and re-fires after any toggle. Returns an
 * unsubscribe function.
 */
export function subscribeToDocChecks(docPath: string, listener: Listener): () => void {
  let subs = listeners.get(docPath);
  if (!subs) {
    subs = new Set();
    listeners.set(docPath, subs);
  }
  subs.add(listener);

  // Emit current cached value synchronously so the caller renders with
  // whatever we already know (empty on first sight).
  listener(new Set(cache.get(docPath) ?? new Set<string>()));

  // Kick off a fetch if we haven't loaded this doc yet.
  if (!cache.has(docPath)) {
    void ensureFetched(docPath);
  }

  return () => {
    const s = listeners.get(docPath);
    if (!s) return;
    s.delete(listener);
    if (s.size === 0) listeners.delete(docPath);
  };
}
